import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { Check, Copy, Download, Maximize2 } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

import { dataToCSV, dataToTSV, downloadAsCSV, copyToClipboard, type TableData } from './lib';

interface TableWrapperProps {
  children: React.ReactNode;
  className?: string;
  messageUuid?: string;
  prefix?: string;
  onExpandTable?: (tableData: TableData) => void;
}

export function TableWrapper({
  children,
  className,
  messageUuid,
  onExpandTable,
  prefix = 'nova',
  ...props
}: TableWrapperProps) {
  const [tableData, setTableData] = useState<TableData>({ headers: [], rows: [] });
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const analytics = useAnalytics();
  const tableRef = useRef<HTMLTableElement>(null);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (tableRef.current) {
      const extractTableData = () => {
        const table = tableRef.current;
        if (!table) return;

        const headers: string[] = [];
        const rows: string[][] = [];

        // Extract headers
        const headerCells = table.querySelectorAll('thead th');
        headerCells.forEach((cell) => {
          headers.push(cell.textContent?.trim() || '');
        });

        // Extract rows
        const bodyRows = table.querySelectorAll('tbody tr');
        bodyRows.forEach((row) => {
          const rowData: string[] = [];
          const cells = row.querySelectorAll('td');
          cells.forEach((cell) => {
            rowData.push(cell.textContent?.trim() || '');
          });
          if (rowData.length > 0) {
            rows.push(rowData);
          }
        });

        setTableData({ headers, rows });
      };

      // Extract data after DOM is ready - increase delay to ensure full rendering
      const timer = setTimeout(extractTableData, 100);
      return () => clearTimeout(timer);
    }
  }, [children]);

  const handleCopy = async (inFullscreen: boolean) => {
    if (tableData.headers.length === 0) return;

    analytics.track({
      event: 'nova-table-copied',
      properties: {
        inFullscreen,
        messageUuid,
      },
    });

    setCopying(true);
    const tsvContent = dataToTSV(tableData.headers, tableData.rows);
    await copyToClipboard(tsvContent);
    setCopying(false);
    setCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = window.setTimeout(() => {
      setCopied(false);
      copyTimeoutRef.current = null;
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleDownload = (inFullscreen: boolean) => {
    analytics.track({
      event: `${prefix}-table-downloaded`,
      properties: {
        inFullscreen,
        messageUuid,
      },
    });

    if (tableData.headers.length === 0) return;

    const csvContent = dataToCSV(tableData.headers, tableData.rows);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    downloadAsCSV(`table-export-${timestamp}.csv`, csvContent);
  };

  const hasExpandCallback = !!onExpandTable;

  const isDisabled = tableData.headers.length === 0;
  return (
    <div className="my-4 w-full">
      <div className="border-steel-200 w-full max-w-full overflow-x-auto overscroll-x-contain rounded-xs border">
        <table
          ref={tableRef}
          className={cn('divide-steel-200 border-steel-200 bg-steel-50 w-max min-w-full divide-y border-0', className)}
          {...props}
        >
          {children}
        </table>
      </div>

      {/* Toolbar */}
      <TooltipProvider>
        <div
          className="border-steel-200 bg-steel-50 flex items-center justify-start gap-2 overflow-x-auto rounded-b-xs border-x border-b p-2"
          data-nova-copy-exclude="true"
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="transparent"
                size="small"
                disabled={isDisabled}
                onClick={() => {
                  analytics.track({
                    event: `${prefix}-table-fullscreen`,
                    properties: {
                      messageUuid,
                    },
                  });
                  if (hasExpandCallback) {
                    onExpandTable(tableData);
                  }

                  setIsDialogOpen(!hasExpandCallback);
                }}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>View table in fullscreen</p>
            </TooltipContent>
          </Tooltip>

          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="max-h-[90vh] max-w-7xl overflow-auto [&>*]:!z-[9999]" style={{ zIndex: 9999 }}>
              <DialogHeader>
                <DialogTitle>Table View</DialogTitle>
              </DialogHeader>
              <div className="overflow-auto">
                <table className="divide-steel-200 border-steel-200 bg-steel-50 min-w-full divide-y rounded-xs border">
                  <thead className="bg-steel-100 rounded-xs">
                    <tr>
                      {tableData.headers.map((header, headerIndex) => (
                        <th
                          key={`header-${headerIndex}-${header}`}
                          className="border-steel-200 text-steel-500 border-r px-4 py-3 text-left text-xs font-medium tracking-wider whitespace-nowrap uppercase last:border-r-0"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-steel-200 divide-y bg-white">
                    {tableData.rows.map((row, rowIndex) => (
                      <tr key={`row-${rowIndex}-${row.join('-')}`} className="hover:bg-steel-50">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={`cell-${rowIndex}-${cellIndex}-${cell}`}
                            className="border-steel-200 text-steel-500 border-r px-4 py-3 text-sm whitespace-nowrap last:border-r-0"
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex justify-start gap-2">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => handleCopy(true)}
                  disabled={copying || isDisabled}
                >
                  {copied ? <Check className="mr-1 h-4 w-4 text-green-600" /> : <Copy className="mr-1 h-4 w-4" />}
                  {copying ? 'Copying...' : copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button variant="secondary" size="small" onClick={() => handleDownload(true)} disabled={isDisabled}>
                  <Download className="mr-1 h-4 w-4" />
                  Download CSV
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="transparent"
                size="small"
                onClick={() => handleCopy(false)}
                disabled={copying || isDisabled}
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy table to clipboard</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="transparent" size="small" onClick={() => handleDownload(false)} disabled={isDisabled}>
                <Download className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Download table as CSV</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </div>
  );
}
